import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { CalendarEvent } from "@/pages/Calendario"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Calendar as CalendarIcon, Clock, MapPin, Tag, AlertCircle, Edit, Trash2 } from "lucide-react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useState } from "react"

interface EventDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: CalendarEvent | null
  onEdit: (event: CalendarEvent) => void
  onDelete: (eventId: string) => Promise<void>
}

export function EventDetailsDialog({ open, onOpenChange, event, onEdit, onDelete }: EventDetailsDialogProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!event) return null

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(event.id)
      setShowDeleteDialog(false)
    } finally {
      setDeleting(false)
    }
  }

  const eventTypeLabels = {
    task: "Tarefa",
    meeting: "Reunião",
    vistoria: "Vistoria",
    instalacao: "Instalação",
    other: "Outro",
  }

  const statusLabels = {
    pending: "Pendente",
    confirmed: "Confirmado",
    cancelled: "Cancelado",
    completed: "Concluído",
  }

  const statusColors: Record<CalendarEvent["status"], string> = {
    pending: "warning",
    confirmed: "success",
    cancelled: "destructive",
    completed: "secondary",
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="text-2xl">{event.title}</DialogTitle>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">
                    {eventTypeLabels[event.event_type]}
                  </Badge>
                  <Badge variant={statusColors[event.status] as any}>
                    {statusLabels[event.status]}
                  </Badge>
                  {event.google_calendar_id && (
                    <Badge variant="secondary">
                      Sincronizado com Google
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Data e Hora */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4" />
                <span>Data e Hora</span>
              </div>
              <div className="ml-6 text-sm text-muted-foreground">
                {event.allDay ? (
                  <>
                    <p>{format(event.start, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
                    {event.start.toDateString() !== event.end.toDateString() && (
                      <p>até {format(event.end, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
                    )}
                    <p className="text-xs mt-1">(Dia inteiro)</p>
                  </>
                ) : (
                  <>
                    <p>{format(event.start, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
                    <p className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Descrição */}
            {event.description && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Tag className="h-4 w-4" />
                  <span>Descrição</span>
                </div>
                <div className="ml-6 text-sm text-muted-foreground whitespace-pre-wrap">
                  {event.description}
                </div>
              </div>
            )}

            {/* Local */}
            {event.location && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="h-4 w-4" />
                  <span>Local</span>
                </div>
                <div className="ml-6 text-sm text-muted-foreground">
                  {event.location}
                </div>
              </div>
            )}

            {/* Lembrete */}
            {event.reminder_minutes && event.reminder_minutes > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>Lembrete</span>
                </div>
                <div className="ml-6 text-sm text-muted-foreground">
                  {event.reminder_minutes < 60
                    ? `${event.reminder_minutes} minutos antes`
                    : event.reminder_minutes < 1440
                    ? `${event.reminder_minutes / 60} hora(s) antes`
                    : `${event.reminder_minutes / 1440} dia(s) antes`}
                </div>
              </div>
            )}

            {/* Cor personalizada */}
            {event.color && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Tag className="h-4 w-4" />
                  <span>Cor personalizada</span>
                </div>
                <div className="ml-6 flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: event.color }}
                  />
                  <span className="text-sm text-muted-foreground">{event.color}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Excluir
            </Button>
            <Button
              type="button"
              onClick={() => onEdit(event)}
              className="gap-2"
            >
              <Edit className="h-4 w-4" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O evento "{event.title}" será excluído permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}



